import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";
import dayjs from "dayjs";

const environment = getEnvironment();

export enum BookingState {
    Wait = "wait",
    Done = "done",
    Cancel = "cancel",
    Blocked = "blocked",
}

export default class Booking {
    key: string;
    service: string;
    user: string;
    duration: number;
    price: number;
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    state: BookingState;
    confirmed: boolean;
    miaCandidate: boolean;
    discount: number;
    note?: string;
    contacts?: {
        name: string | null;
        email: string | null;
        phone: string | null;
    };

    constructor(
        key: string,
        service: string,
        user: string,
        duration: number,
        price: number,
        start: dayjs.Dayjs,
        end: dayjs.Dayjs,
        state: BookingState = BookingState.Wait,
        confirmed: boolean = false,
        miaCandidate: boolean = false,
        discount: number,
        note?: string,
        contacts?: {
            name: string | null;
            email: string | null;
            phone: string | null;
        }
    ) {
        this.key = key;
        this.service = service;
        this.user = user;
        this.duration = duration;
        this.price = price;
        this.start = start;
        this.end = end;
        this.state = state;
        this.confirmed = confirmed;
        this.miaCandidate = miaCandidate;
        this.discount = discount;
        this.note = note;
        this.contacts = contacts;
    }

    public static readonly register = async (
        uuid: string,
        brand: string,
        location: string,
        service: string,
        user: string,
        duration: number,
        price: number,
        start: Date,
        end: Date,
        confirmed: boolean = false,
        state: BookingState = BookingState.Wait,
        note?: string
    ) =>
        (
            await axios
                .post(
                    environment.api_url +
                        "/" +
                        brand +
                        "/location/" +
                        location +
                        "/booking",
                    {
                        service,
                        user,
                        duration,
                        price,
                        start: start.toISOString(),
                        end: end.toISOString(),
                        state,
                        confirmed,
                        note,
                    },
                    {
                        headers: {
                            "User-Token": uuid,
                        },
                    }
                )
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)
        ).key as string | undefined;

    public static readonly update = async (
        uuid: string,
        brand: string,
        location: string,
        booking: string,
        start: Date,
        end: Date
    ) =>
        axios.put(
            environment.api_url +
                "/" +
                brand +
                "/location/" +
                location +
                "/booking/" +
                booking,
            {
                start: start.toISOString(),
                end: end.toISOString(),
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly updateState = async (
        uuid: string,
        brand: string,
        location: string,
        booking: string,
        state: BookingState,
        start?: Date,
        end?: Date
    ) =>
        axios.put(
            environment.api_url +
                "/" +
                brand +
                "/location/" +
                location +
                "/booking/" +
                booking,
            {
                state,
                start: start?.toISOString(),
                end: end?.toISOString(),
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly updateConfirmed = async (
        uuid: string,
        brand: string,
        location: string,
        booking: string,
        confirmed: boolean
    ) =>
        axios.put(
            environment.api_url +
                "/" +
                brand +
                "/location/" +
                location +
                "/booking/" +
                booking,
            {
                confirmed,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly updateNote = async (
        uuid: string,
        brand: string,
        location: string,
        booking: string,
        note: string
    ) =>
        axios.put(
            environment.api_url +
                "/" +
                brand +
                "/location/" +
                location +
                "/booking/" +
                booking,
            {
                note,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );

    public static readonly list = async (
        uuid: string,
        brand: string,
        location: string
    ) =>
        (await axios
            .get(
                environment.api_url +
                    "/" +
                    brand +
                    "/location/" +
                    location +
                    "/booking/list",
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as Booking[] | undefined;

    public static readonly info = async (
        uuid: string,
        brand: string,
        location: string,
        booking: string,
        withContacts = false
    ) => {
        const query = new URLSearchParams();
        if (withContacts) query.append("withContacts", "true");

        return (await axios
            .get(
                environment.api_url +
                    "/" +
                    brand +
                    "/location/" +
                    location +
                    "/booking/" +
                    booking +
                    (query.toString() ? "?" + query.toString() : ""),
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as Booking | undefined;
    };

    public static readonly listPeriod = async (
        uuid: string,
        brand: string,
        location: string,
        year: number,
        month: number,
        day?: number
    ) =>
        (await axios
            .get(
                environment.api_url +
                    "/" +
                    brand +
                    "/location/" +
                    location +
                    "/booking/period/" +
                    year +
                    "/" +
                    month +
                    (day !== undefined ? "/" + day : "") +
                    "/list",
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as Booking[] | undefined;

    public static readonly listUser = async (
        uuid: string,
        brand: string,
        location: string,
        type: "active" | "waiting" | "history",
        user: string
    ) =>
        (await axios
            .get(
                environment.api_url +
                    "/" +
                    brand +
                    "/location/" +
                    location +
                    "/booking/" +
                    type +
                    "/list",
                {
                    headers: {
                        "User-Token": uuid,
                    },
                    params: {
                        user,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as Booking[] | undefined;

    public static readonly blockSlot = async (
        uuid: string,
        brand: string,
        location: string,
        start: Date,
        end: Date,
        note?: string
    ) =>
        (
            await axios
                .post(
                    `${environment.api_url}/${brand}/location/${location}/booking`,
                    {
                        price: 0, // Blocked slots have no price
                        start: start.toISOString(),
                        end: end.toISOString(),
                        state: BookingState.Blocked, // Set to 'blocked'
                        confirmed: false,
                        note,
                    },
                    {
                        headers: {
                            "User-Token": uuid,
                        },
                    }
                )
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)
        ).key as string | undefined;

    public static readonly updateBlockedSlot = async (
        uuid: string,
        brand: string,
        location: string,
        slotKey: string,
        start: Date,
        end: Date,
        note?: string
    ): Promise<boolean> => {
        try {
            await axios.put(
                `${environment.api_url}/${brand}/location/${location}/booking/${slotKey}/blocked-slot`,
                {
                    start: start.toISOString(),
                    end: end.toISOString(),
                    note: note ?? null,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            );
            return true;
        } catch (error) {
            console.error("Error updating blocked slot:", error);
            return false;
        }
    };

    public static readonly deleteBlockedSlot = async (
        uuid: string,
        brand: string,
        location: string,
        booking: string,
        mode: "single" | "all" = "single"
    ) => {
        try {
            await axios.delete(
                `${environment.api_url}/${brand}/location/${location}/booking/${booking}?mode=${mode}`,
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            );
            return true; // Successfully deleted
        } catch (error) {
            console.error("Error deleting blocked slot:", error);
            return false; // Deletion failed
        }
    };

    public static readonly makeRecurringFromSlot = async (
        uuid: string,
        brand: string,
        location: string,
        originalSlotKey: string,
        interval: "weekly" | "monthly"
    ): Promise<
        { success: boolean; inserted: number; skipped: number } | undefined
    > =>
        await axios
            .post(
                `${environment.api_url}/${brand}/location/${location}/booking/${originalSlotKey}/recurring`,
                { interval },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch((err) => {
                console.error("Error making recurring slot:", err);
                return undefined;
            });
}
